import { normalizePhotoPosition, PHOTO_POSITION_CLASS } from "@/lib/avatar";

interface Props {
  snapshot: Record<string, unknown> | null | undefined;
  onClose: () => void;
}

const SNAPSHOT_FIELDS: { key: string; label: string; icon: string }[] = [
  { key: "full_name", label: "Nome", icon: "ri-user-line" },
  { key: "email", label: "E-mail", icon: "ri-mail-line" },
  { key: "whatsapp", label: "WhatsApp", icon: "ri-whatsapp-line" },
  { key: "country_code", label: "Código do país", icon: "ri-global-line" },
  { key: "city_state", label: "Cidade / Estado", icon: "ri-map-pin-line" },
  { key: "profession", label: "Profissão", icon: "ri-briefcase-line" },
  { key: "description", label: "Resumo", icon: "ri-file-text-line" },
  { key: "bio_long", label: "Bio completa", icon: "ri-quill-pen-line" },
  { key: "instagram", label: "Instagram", icon: "ri-instagram-line" },
  { key: "website", label: "Website", icon: "ri-global-line" },
  { key: "linkedin", label: "LinkedIn", icon: "ri-linkedin-line" },
  { key: "facebook", label: "Facebook", icon: "ri-facebook-line" },
  { key: "tiktok", label: "TikTok", icon: "ri-tiktok-line" },
  { key: "skills", label: "Habilidades", icon: "ri-star-line" },
  { key: "languages", label: "Idiomas", icon: "ri-translate-2" },
  { key: "education", label: "Formação", icon: "ri-graduation-cap-line" },
  { key: "services", label: "Serviços", icon: "ri-service-line" },
  { key: "experience_years", label: "Experiência", icon: "ri-timer-line" },
  { key: "business_name", label: "Nome do negócio", icon: "ri-store-2-line" },
  { key: "business_address", label: "Endereço do negócio", icon: "ri-map-pin-2-line" },
  { key: "business_city", label: "Cidade do negócio", icon: "ri-building-2-line" },
  { key: "business_state", label: "Estado do negócio", icon: "ri-flag-2-line" },
  { key: "business_zip", label: "CEP do negócio", icon: "ri-road-map-line" },
];

function formatValue(v: unknown): string {
  if (v == null) return "";
  if (typeof v === "boolean") return v ? "Sim" : "Não";
  return String(v);
}

export default function OriginalSnapshotModal({ snapshot, onClose }: Props) {
  const photo = snapshot?.photo ? String(snapshot.photo) : "";
  const photoPosition = snapshot?.photo_position ? String(snapshot.photo_position) : "top";
  const createdAt = snapshot?.created_at ? String(snapshot.created_at) : "";
  const fullName = snapshot?.full_name ? String(snapshot.full_name) : "Cadastro";

  const rows = SNAPSHOT_FIELDS.map((f) => ({ ...f, value: formatValue(snapshot?.[f.key]) })).filter(
    (r) => r.value,
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-6 bg-black/50">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto flex flex-col">
        <div className="flex items-center justify-between gap-4 px-6 py-5 border-b border-nude sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-display text-lg text-brown">Cadastro original</h3>
            <p className="font-body text-xs text-brown/50 mt-0.5">
              Retrato completo de <strong>{fullName}</strong> antes da mesclagem (somente leitura).
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full text-brown/40 hover:text-brown hover:bg-beige transition-colors cursor-pointer shrink-0"
            title="Fechar"
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {photo && (
            <div className="mb-4 flex items-center gap-3">
              <img
                src={photo}
                alt={fullName}
                className={`w-16 h-16 rounded-xl object-cover ${PHOTO_POSITION_CLASS[normalizePhotoPosition(photoPosition)]}`}
              />
              <div>
                <p className="font-body text-[11px] text-brown/40 uppercase tracking-wide">Foto original</p>
                <p className="font-body text-sm text-brown">{fullName}</p>
              </div>
            </div>
          )}

          {rows.length === 0 ? (
            <p className="font-body text-sm text-brown/50">
              Nenhum dado original disponível para este cadastro.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {rows.map((r) => (
                <div key={r.key} className="flex items-start gap-2.5 p-3 rounded-xl bg-beige/60">
                  <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary/10 shrink-0 mt-0.5">
                    <i className={`${r.icon} text-xs text-primary`} />
                  </div>
                  <div className="min-w-0">
                    <p className="font-body text-[11px] text-brown/40 uppercase tracking-wide mb-0.5">{r.label}</p>
                    <p className="font-body text-sm text-brown break-words">{r.value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {createdAt && (
            <p className="font-body text-xs text-brown/40 mt-4">
              Cadastrada em {new Date(createdAt).toLocaleString("pt-BR")}
            </p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-nude sticky bottom-0 bg-white">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors cursor-pointer whitespace-nowrap"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}